import React, { useState, createContext, useContext, useEffect, useRef } from "react";
import { useTheme } from "styled-components";
import { useUploadMedia } from "@/utils/react-query-hooks/media";
import { useBannerContext } from "oolib";

export const ImageInputContext = createContext();



export const ImageInputContextProvider = ({
  children,
  value,
  multiple,
  allowedFormats,
  formatConvertConfig,
  folderName,
  bucketEnv,
  onChange,
  id,
  aspectRatio,
  readOnly,
  enableCaptions,
  defaultImageSpread,
  invert,
  containerShape,
  stretchToFullHeight,
  dropzoneLabel,
  dropzoneSublabel,
  disableImageBorder,
  omitEditorTools,
  files,
  showOptionalLabelInDropzone,
  isRequired,
  isInRTE,
  showImageUploadCaution,
  imageTags
}) => {

  const theme = useTheme()

  const [uploadProgress, setUploadProgress] = useState(undefined);
  const handleUploadProgess = (progress) => setUploadProgress(progress);

  const { mutate: upload, isLoading } = useUploadMedia({
    mediaConfigs: {  
      allowedFormats, 
      formatConvertConfig,
      folderName,
      bucketEnv,
      mediaType: "image" 
    },
    handleUploadProgess,
  }) 
  

  const { SET_ALERT_BANNER } = useBannerContext()

  const [noOfImagesUploading, setNoOfImagesUploading] = useState(0);
  const [uploadArgs, setUploadArgs] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [publicUrlBeingReplaced, setPublicUrlBeingReplaced] =
    useState(undefined);

  const handleUpload = ({ files, callback, publicUrlToReplace }) => {
    //publicUrlToReplace passed only if replace action
    if (!files) return;

    publicUrlToReplace
      ? setPublicUrlBeingReplaced(publicUrlToReplace)
      : setNoOfImagesUploading(files?.length || 0);

    upload(files, {
      onSuccess(data) {
        //extract out all the successful uploads' fileData
        const successfulUploads = data.reduce(
          (result, d) =>
            d.status === "success" ? [...result, d.fileData] : result,
          []
        );

        //error banner for all failed uploads
        data.forEach(
          (d) =>
            d.status === "error" && SET_ALERT_BANNER(d.message, "red", 3500)
        );

        callback({ successfulUploads });

        publicUrlToReplace
          ? setPublicUrlBeingReplaced(undefined)
          : setNoOfImagesUploading(0);
      },
      onError(err) {
        SET_ALERT_BANNER(
          err.response?.data.errors[0].msg || err.message,
          "red",
          4000
        );

        publicUrlToReplace
          ? setPublicUrlBeingReplaced(undefined)
          : setNoOfImagesUploading(0);
      },
    });
  };

  const handleUploadWithCheck = (args) => {

    const item = localStorage.getItem("PreUploadImageCautionAcknowleged");

    if (item && JSON.parse(item)) {
      handleUpload({ ...args });
      
    } else {
      setUploadArgs(args);
      setShowModal(true);
      
    }
  };

  /**
   * this is specifically required in the RTE scenario,
   * wherein, the minute files are uploaded from the RTE,
   * we want to generate the ImageInput, so that things like
   * loading state, errors etc, are handled internally.
   * 
   * Very important to remember though, that in other cases,
   * files prop SHOULD NOT BE PASSED, else everytime the comp
   * renders for the first time, it will upload said files to
   * cloud storage.
   */
  useEffect(() => {
    if(files){
      handleUpload({
        files,
        callback: ({successfulUploads}) => {
          onChange(
            id,
            multiple
              ? [...successfulUploads, ...(value || [])] 
              : successfulUploads
          )
        }
      })
    }
  },[])

  // Create a ref to always track the latest value
  const valueRef = useRef(value);
    
  // Keep the ref updated whenever value changes
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  const handleSingleImageDataChange = ({ publicUrl, imageData }) => {
    //publicUrl is used as an 'id' identifier
    const currentValue = valueRef.current;
    const newValue = [...currentValue];
    const imgIdxToReplace = newValue.findIndex(
      (d) => d.publicUrl === publicUrl
    );

    newValue[imgIdxToReplace] = imageData;
    
    onChange(id, newValue);
  };

  const handleDeleteSingleImage = ({ publicUrl }) => {
    let newValue = [...value];
    newValue = newValue.filter((d) => d.publicUrl !== publicUrl);
    onChange(id, newValue);
  };

  const handleReplaceSingleImage = ({ publicUrl, newImage }) => {
    const newValue = [...value];
    let idx = newValue.findIndex((d) => d.publicUrl === publicUrl);
    newValue[idx] = newImage;
    onChange(id, newValue);
  };

  const [imageUnderEdit, setImageUnderEdit] = useState(false);

  return (
    <ImageInputContext.Provider
      value={{
        //props
        id,
        value,
        onChange,
        multiple,
        allowedFormats,
        aspectRatio,
        readOnly,
        enableCaptions,
        imageTags,
        defaultImageSpread,
        invert,
        containerShape,
        stretchToFullHeight,
        dropzoneLabel,
        dropzoneSublabel,
        disableImageBorder,
        omitEditorTools,

        //handlers, states initiated in this Context
        upload,
        handleUpload: showImageUploadCaution.enable
          ? handleUploadWithCheck
          : handleUpload,
        noOfImagesUploading,
        publicUrlBeingReplaced,
        uploadProgress,
        isLoading,
        handleSingleImageDataChange,
        handleDeleteSingleImage,
        handleReplaceSingleImage,
        imageUnderEdit,
        setImageUnderEdit,
        showOptionalLabelInDropzone,
        isRequired,
        isInRTE,
        uploadArgs,
        showModal,
        setShowModal,
        showImageUploadCaution
      }}
    >
      {children}
    </ImageInputContext.Provider>
  );
};

export const useImageInputContext = () => {
  return useContext(ImageInputContext);
};
